import React from "react";
import { Core } from "../../utilities/core";
import { apiIds, sortCaseFeedItemsByDate } from "./general";
import { CaseFeedRecord, CaseItem, EmailFeedRecord } from "./types";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import Card from "@mui/joy/Card";
import ModalClose from "@mui/joy/ModalClose";
import Typography, { typographyClasses } from "@mui/joy/Typography";
import Divider from "@mui/joy/Divider";
import Box from "@mui/joy/Box";
import Stepper from "@mui/joy/Stepper";
import Step, { stepClasses } from "@mui/joy/Step";
import StepIndicator, { stepIndicatorClasses } from "@mui/joy/StepIndicator";
import Stack from "@mui/joy/Stack";
import Chip from "@mui/joy/Chip";
import CardOverflow from "@mui/joy/CardOverflow";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Textarea from "@mui/joy/Textarea";
import CardActions from "@mui/joy/CardActions";
import Button from "@mui/joy/Button";
import CircularProgress from "@mui/joy/CircularProgress";
import Input from "@mui/joy/Input";
import Skeleton from "@mui/joy/Skeleton";
import ListIcon from "@mui/icons-material/List";
import Alert from "@mui/joy/Alert";
import ReportIcon from "@mui/icons-material/Report";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import { AccountInfo, PublicClientApplication } from "@azure/msal-browser";
import { createSafeHtml } from "../../utilities/sanitization";

/**
 * Maps technical field names to user-friendly labels
 */
const FIELD_LABELS: Record<string, string> = {
  CaseNumber: "Case Number",
  Subject: "Subject",
  Status: "Status",
  Priority: "Priority",
  LastModifiedDate: "Last Updated",
  Category__c: "Category",
  Agent__c: "Agent ID",
  Agents_First_Name__c: "Agent Name",
  Module__c: "Module",
  Incident_type__c: "Incident Type",
  Id: "Case ID",
};

/**
 * Creates user-friendly error messages based on error type and context
 */
function getErrorMessage(error: any, context: "feed" | "reply"): string {
  // Check for specific error types
  if (error?.status === 401 || error?.message?.includes("unauthorized")) {
    return "Your session has expired. Please refresh the page and try again.";
  }

  if (error?.status === 403) {
    return "You don't have permission to access this ticket. Please contact your administrator.";
  }

  if (error?.status === 404) {
    return context === "feed" ? "This ticket could not be found. It may have been deleted or moved." : "Unable to send reply. The ticket may no longer exist.";
  }

  if (error?.status === 500 || error?.message?.includes("server")) {
    return "The server is experiencing issues. Please try again in a few minutes.";
  }

  if (!navigator.onLine) {
    return "You appear to be offline. Please check your internet connection and try again.";
  }

  // Generic fallback messages
  return context === "feed"
    ? "Unable to load the ticket conversation. Please refresh and try again."
    : "Failed to send your reply. Please check your message and try again.";
}

/**
 * Extracts the latest message reply content from email text body
 * Takes everything above the separator line (________________________________)
 */
function extractLastestMessageReply(textBody: string): string {
  if (!textBody || typeof textBody !== "string") {
    return "";
  }

  let processed = textBody;

  // Find the separator line and take everything above it
  const separatorMatch = processed.split(/_{10,}/);
  if (separatorMatch.length > 0) {
    processed = separatorMatch[0];
  }

  // Remove thread identifiers
  processed = processed.replace(/thread::.+?::/g, "");

  // Remove Salesforce image server URLs
  processed = processed.replace(/\[https:\/\/[^[\]]*servlet\.ImageServer[^\]]*\]/g, "");

  // Clean up multiple consecutive newlines and convert to HTML breaks
  processed = processed.replace(/\n{2,}/g, "</br>");

  // Remove extra whitespace
  processed = processed.trim();

  return processed;
}

type TicketModalType = {
  account: AccountInfo;
  instance: PublicClientApplication;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  caseItem?: CaseItem;
};

/**
 * Ticket Modal Component that will retrieve and Display feeds
 */
export function TicketModal({ account, instance, showModal, setShowModal, caseItem }: TicketModalType) {
  const [mounted, setMounted] = React.useState<boolean>(false);
  const [core, setCore] = React.useState<Core>();
  const [feedItems, setFeedItems] = React.useState<CaseFeedRecord[]>();
  const [reply, setReply] = React.useState<string>();
  const [sendingReply, setSendingReply] = React.useState<boolean>();
  const [refreshFeeds, setRefreshFeeds] = React.useState<boolean>();
  const [loadingFeeds, setLoadingFeeds] = React.useState<boolean>(false);
  const [refreshingFeeds, setRefreshingFeeds] = React.useState<boolean>(false);
  const [feedError, setFeedError] = React.useState<string | null>(null);
  const [replyError, setReplyError] = React.useState<string | null>(null);
  const [replySuccess, setReplySuccess] = React.useState<boolean>(false);
  const replyTextareaRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (mounted && account && instance) {
      new Core().initialiseCore(instance, account).then((core) => {
        setCore(core);
      });
    }
  }, [mounted, account, instance]);

  const handleCloseModal = React.useCallback(() => {
    // Clean up state when modal closes
    setFeedItems(undefined);
    setReply("");
    setFeedError(null);
    setReplyError(null);
    setReplySuccess(false);
    setLoadingFeeds(false);
    setRefreshingFeeds(false);
    setSendingReply(false);
    setShowModal(false);
  }, [setShowModal]);

  const handleSubmitReply = React.useCallback(async () => {
    if (caseItem?.Id && reply?.trim() && reply.length <= 1000) {
      setSendingReply(true);
      setReplyError(null);
      setReplySuccess(false);

      try {
        await core?.invokeIntegrationHubRequest("POST", "/salesforce/ticket/feed", {
          fields: {
            ParentId: caseItem.Id,
            Body: reply.trim(),
          },
        });

        // Success!
        setReplySuccess(true);
        setReply("");
        setRefreshFeeds(!refreshFeeds);

        // Hide success message after 3 seconds
        setTimeout(() => {
          setReplySuccess(false);
        }, 3000);
      } catch (err) {
        console.error("Error sending reply:", err);
        setReplyError(getErrorMessage(err, "reply"));
      } finally {
        setSendingReply(false);
      }
    }
  }, [caseItem?.Id, reply, core, refreshFeeds]);

  React.useEffect(() => {
    if (caseItem?.Id && core) {
      // Use different loading states for initial load vs refresh
      const isRefresh = feedItems !== undefined;
      if (isRefresh) {
        setRefreshingFeeds(true);
      } else {
        setLoadingFeeds(true);
      }
      setFeedError(null);

      core
        .invokeIntegrationHubRequest("GET", `/salesforce/ticket/feed?ticketNumber=${caseItem?.Id}`)
        .then((resp) => {
          try {
            const allFeeds = [
              ...((resp.body as any)[0]?.Feeds?.records || []),
              ...((resp.body as any)[0]?.Emails?.records || []).map((e: EmailFeedRecord) => {
                return {
                  attributes: {
                    type: "CaseFeed",
                    url: "",
                  },
                  Body: e.TextBody ? extractLastestMessageReply(e.TextBody) : "",
                  CreatedById: e.CreatedById,
                  LastModifiedDate: e.LastModifiedDate,
                  Type: "TextPost",
                } as CaseFeedRecord;
              }),
            ].filter((item) => ["TextPost", "ContentPost"].includes(item.Type) && item.Body);
            setFeedItems(sortCaseFeedItemsByDate(allFeeds as CaseFeedRecord[]).reverse());
            setFeedError(null); // Clear any previous errors on success
          } catch (err) {
            console.error("Error processing feed data:", err);
            setFeedError(getErrorMessage(err, "feed"));
          }
        })
        .catch((err) => {
          console.error("Error fetching feeds:", err);
          setFeedError(getErrorMessage(err, "feed"));
        })
        .finally(() => {
          setLoadingFeeds(false);
          setRefreshingFeeds(false);
        });
    }
  }, [caseItem, refreshFeeds]);

  // Auto-focus reply textarea after feeds are loaded
  React.useEffect(() => {
    if (feedItems && !loadingFeeds && !feedError && replyTextareaRef.current) {
      // Small delay to ensure modal is fully rendered
      const timer = setTimeout(() => {
        replyTextareaRef.current?.focus();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [feedItems, loadingFeeds, feedError]);

  return (
    <Modal open={showModal} onClose={handleCloseModal} aria-labelledby="ticket-modal-title" aria-describedby="ticket-modal-description">
      <ModalDialog
        variant={"soft"}
        sx={{
          p: 0,
          mx: 0,
          mt: {
            xs: 4,
            sm: 0,
          },
          mb: {
            xs: 1,
            sm: 0,
          },
          width: {
            xs: "100%",
            sm: "auto",
          },
          minWidth: {
            xs: "100%",
            sm: "50vw",
          },
          maxWidth: {
            xs: "100%",
            sm: "90vw",
          },
          maxHeight: {
            xs: "calc(100vh-10vh)",
            sm: "90vh",
          },
          overflow: "auto",
        }}
      >
        <Card>
          <ModalClose variant="solid" color="danger">
            Close
          </ModalClose>
          <Typography level="title-md" id="ticket-modal-title">
            Ticket Details - {caseItem?.CaseNumber || "Unknown"}
          </Typography>
          <Divider />
          <Box
            key={0}
            sx={{
              padding: 0,
              display: "grid",
              justifyContent: "space-between",
              gridTemplateColumns: {
                xs: "100%",
                sm: "50% 50%",
                lg: "repeat(auto-fit, 32%) 32%",
              },
              mb: 2,
              "& > hr": {
                gridColumn: "1/-1",
              },
            }}
          >
            {!caseItem
              ? // Show skeleton loading when case data is not yet available
                Array.from({ length: 6 }).map((_, index) => (
                  <Box key={index} sx={{ padding: 0 }}>
                    <Skeleton variant="text" sx={{ mt: 2, mb: 1 }} width="60%" height="1.2em" />
                    <Skeleton variant="rectangular" height="2.5em" sx={{ borderRadius: "sm" }} />
                  </Box>
                ))
              : showModal &&
                Object.keys(caseItem).map((key) => {
                  if ((caseItem as any)[key] !== null && key !== "attributes" && !key.toLowerCase().includes("description")) {
                    return (
                      <Box
                        key={key}
                        sx={{
                          padding: 0,
                        }}
                      >
                        <Typography mt={2} level="title-sm">
                          {FIELD_LABELS[key] || key.split("__c").join("").split("_").join(" ")}
                        </Typography>
                        <Input
                          readOnly
                          value={
                            key.toLowerCase().includes("date")
                              ? new Date((caseItem as any)[key]).toLocaleString()
                              : typeof (caseItem as any)[key] === "string"
                              ? decodeURI((caseItem as any)[key])
                              : (caseItem as any)[key]?.toString() || ""
                          }
                        />
                      </Box>
                    );
                  }
                })}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography level="title-md" id="ticket-modal-description">
              Ticket Feed
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {refreshingFeeds ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size="sm" />
                  <Typography level="body-xs" color="neutral">
                    Refreshing...
                  </Typography>
                </Box>
              ) : (
                <Button
                  size="sm"
                  variant="soft"
                  color="neutral"
                  disabled={loadingFeeds}
                  onClick={() => setRefreshFeeds(!refreshFeeds)}
                  startDecorator={<RefreshIcon />}
                  sx={{
                    minWidth: "auto",
                    px: 1.5,
                  }}
                >
                  Refresh
                </Button>
              )}
            </Box>
          </Box>
          <Divider></Divider>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              overflowY: "auto",
              maxHeight: {
                xs: "35vh",
                sm: "50vh",
              },
              position: "relative",
              ...(refreshingFeeds && {
                opacity: 0.7,
              }),
            }}
          >
            {loadingFeeds ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4 }}>
                <CircularProgress size="lg" />
                <Typography level="body-sm" sx={{ mt: 2 }}>
                  Loading conversation...
                </Typography>
              </Box>
            ) : feedError ? (
              <Alert
                color="danger"
                startDecorator={<ReportIcon />}
                endDecorator={
                  <Button
                    size="sm"
                    variant="soft"
                    color="danger"
                    disabled={refreshingFeeds}
                    onClick={() => setRefreshFeeds(!refreshFeeds)}
                    startDecorator={refreshingFeeds ? <CircularProgress size="sm" /> : <RefreshIcon />}
                  >
                    {refreshingFeeds ? "Retrying..." : "Retry"}
                  </Button>
                }
                sx={{ mb: 2 }}
              >
                {feedError}
              </Alert>
            ) : (
              <Stepper
                orientation="vertical"
                sx={(theme) => ({
                  "--Stepper-verticalGap": "2rem",
                  "--StepIndicator-size": "2rem",
                  "--Step-gap": "1rem",
                  "--Step-connectorInset": "0",
                  "--Step-connectorRadius": "0",
                  "--Step-connectorThickness": "0",
                  "--joy-palette-success-solidBg": "var(--joy-palette-success-400)",
                  [`& .${stepClasses.completed}`]: {
                    "&::after": { bgcolor: "neutral.solidBg" },
                  },
                  [`& .${stepClasses.active}`]: {
                    [`& .${stepIndicatorClasses.root}`]: {
                      border: "4px solid",
                      borderColor: "#fff",
                      boxShadow: `0 0 0 1px ${theme.vars.palette.primary[500]}`,
                    },
                  },
                  [`& .${stepClasses.disabled} *`]: {
                    color: "neutral.softDisabledColor",
                  },
                  [`& .${typographyClasses["title-sm"]}`]: {
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    fontSize: "10px",
                  },
                })}
              >
                <Step
                  completed
                  indicator={
                    <StepIndicator variant="solid" color="primary">
                      <ListIcon />
                    </StepIndicator>
                  }
                >
                  <div>
                    <Typography level="title-sm">Description</Typography>
                    <Typography level="body-sm" sx={{ mt: 1 }}>
                      <span
                        dangerouslySetInnerHTML={createSafeHtml(caseItem?.Description || caseItem?.Case_Description__c || "No description available")}
                      ></span>
                    </Typography>
                  </div>
                </Step>
                <Stack spacing={2}>
                  {feedItems && feedItems.length > 0 ? (
                    feedItems.map((item, index) => (
                      <Typography key={index} level="body-sm" sx={{ alignSelf: apiIds.includes(item.CreatedById) ? "end" : "start" }}>
                        <Chip
                          sx={{
                            px: 2,
                            py: 0.5,
                            textWrap: "auto",
                            "& > * > * > *": {
                              margin: "0.5rem",
                            },
                          }}
                          variant="solid"
                          color={apiIds.includes(item.CreatedById) ? "success" : "primary"}
                          component={"span"}
                        >
                          <span dangerouslySetInnerHTML={createSafeHtml(item.Body || "")}></span>
                        </Chip>
                      </Typography>
                    ))
                  ) : (
                    <Typography level="body-sm" color="neutral" sx={{ textAlign: "center", py: 2 }}>
                      No conversation history available.
                    </Typography>
                  )}
                  <span ref={(element) => element?.scrollIntoView({ behavior: "smooth" })} />
                </Stack>
              </Stepper>
            )}
          </Box>

          <CardOverflow sx={{ borderTop: "1px solid", borderColor: "divider" }}>
            <FormControl
              sx={{
                mt: 2,
              }}
            >
              <FormLabel>
                Reply
                {reply && (
                  <Typography level="body-xs" sx={{ float: "right", color: reply.length > 1000 ? "danger.500" : "neutral.500" }}>
                    {reply.length}/1000
                  </Typography>
                )}
              </FormLabel>
              <Textarea
                ref={replyTextareaRef}
                value={reply}
                onChange={(event) => {
                  setReply(event.target.value);
                  // Clear reply-related states when user starts typing
                  if (replyError) setReplyError(null);
                  if (replySuccess) setReplySuccess(false);
                }}
                onKeyDown={(event) => {
                  // Allow Ctrl+Enter or Cmd+Enter to send reply
                  if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
                    event.preventDefault();
                    if (reply?.trim() && !sendingReply) {
                      handleSubmitReply();
                    }
                  }
                }}
                disabled={sendingReply}
                content="text"
                placeholder="Type your reply here... (Ctrl+Enter to send)"
                minRows={3}
                maxRows={6}
                aria-label="Reply to ticket"
                aria-describedby={replyError ? "reply-error" : replySuccess ? "reply-success" : undefined}
                sx={{
                  ...(sendingReply && {
                    opacity: 0.7,
                  }),
                }}
              />
              {replyError && (
                <Alert color="danger" size="sm" sx={{ mt: 1 }} id="reply-error" role="alert">
                  {replyError}
                </Alert>
              )}
              {replySuccess && (
                <Alert color="success" size="sm" startDecorator={<CheckCircleIcon />} sx={{ mt: 1 }} id="reply-success" role="status">
                  Reply sent successfully!
                </Alert>
              )}
            </FormControl>
            <CardActions sx={{ alignSelf: "flex-end", flexFlow: "row", pt: 2, gap: 1 }}>
              <Button
                variant="outlined"
                color="neutral"
                size="sm"
                disabled={sendingReply || !reply?.trim()}
                onClick={() => {
                  setReply("");
                  setReplyError(null);
                  setReplySuccess(false);
                }}
              >
                Clear
              </Button>
              <Button
                disabled={sendingReply || !reply?.trim() || (reply?.length || 0) > 1000}
                loading={sendingReply}
                loadingPosition="start"
                startDecorator={sendingReply ? undefined : <SendIcon />}
                onClick={handleSubmitReply}
              >
                {sendingReply ? "Sending..." : "Send Reply"}
              </Button>
            </CardActions>
          </CardOverflow>
        </Card>
      </ModalDialog>
    </Modal>
  );
}
